// Shaped severity icon components. The registry + helpers live in ./severity.
//
// Geometry follows the Lucide icon family (circle-help, info, alert-circle,
// alert-triangle, alert-octagon) on a 24x24 grid. Each level is set apart by
// SHAPE and FILL, not colour alone — circle -> triangle -> octagon, outline
// (levels 0-3) -> solid (levels 4-5 + Normal) — so the icons stay legible in
// greyscale and for colour-blind users.

import type { ReactNode } from 'react'
import { SEVERITY, severityMeta, EMOJI_SEV, INLINE_SEV_SOURCE } from './severity'
import type { SeverityShape } from './severity'

// Outer shape outlines (Lucide), closed so they fill cleanly in solid mode.
const TRIANGLE = 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z'
const OCTAGON =
  'M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688' +
  'A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688' +
  'v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688' +
  'a2 2 0 0 1-1.414-.586z'

interface OuterProps {
  shape: SeverityShape
  solid: boolean
  color: string
}

function Outer({ shape, solid, color }: OuterProps) {
  const fill = solid ? color : 'none'
  const common = { fill, stroke: color, strokeWidth: 2, strokeLinejoin: 'round' as const }
  if (shape === 'circle') return <circle cx="12" cy="12" r="10" {...common} />
  if (shape === 'triangle') return <path d={TRIANGLE} {...common} />
  return <path d={OCTAGON} {...common} />
}

interface SymbolProps {
  glyph: string
  shape: SeverityShape
  stroke: string
}

// Inner mark, drawn in white on solid fills or the level colour on outlines.
function Symbol({ glyph, shape, stroke }: SymbolProps) {
  const p = { fill: 'none', stroke, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  if (glyph === 'check') return <path d="M8 12.5l2.6 2.6L16 8.5" {...p} />
  if (glyph === 'i') return <><path d="M12 16v-4" {...p} /><path d="M12 8h.01" {...p} /></>
  if (glyph === '?') return <><path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3" {...p} /><path d="M12 17h.01" {...p} /></>
  // '!' — the alert bang sits lower inside a triangle than a circle/octagon.
  const top = shape === 'triangle' ? 'M12 10v3.5' : 'M12 8v4'
  const dot = shape === 'triangle' ? 'M12 17h.01' : 'M12 16h.01'
  return <><path d={top} {...p} /><path d={dot} {...p} /></>
}

interface SeverityIconProps {
  level: unknown
  size?: number
  title?: string
}

export function SeverityIcon({ level, size = 16, title }: SeverityIconProps) {
  const meta = severityMeta(level) || SEVERITY[0]
  const solid = meta.fill === 'solid'
  const label = title || `${meta.label}${meta.blurb ? ` — ${meta.blurb}` : ''}`
  return (
    <svg className="nw-sev-icon" width={size} height={size} viewBox="0 0 24 24"
         role="img" aria-label={label}>
      <title>{label}</title>
      <Outer shape={meta.shape} solid={solid} color={meta.color} />
      <Symbol glyph={meta.glyph} shape={meta.shape} stroke={solid ? '#fff' : meta.color} />
    </svg>
  )
}

interface SeverityBadgeProps {
  level: unknown
  size?: number
  showLabel?: boolean
}

// Icon + text label, the standard way to show a status inline (tables, rows).
export function SeverityBadge({ level, size = 16, showLabel = true }: SeverityBadgeProps) {
  const meta = severityMeta(level)
  return (
    <span className="nw-sev-badge">
      <SeverityIcon level={level} size={size} />
      {showLabel && meta && <span className="nw-sev-label">{meta.label}</span>}
    </span>
  )
}

interface InlineSeverityProps {
  text: unknown
  size?: number
}

// Render a plain string with any severity emoji / `sev:N` tokens replaced by
// shaped icons. Used for free-text cells that carry an inline severity dot
// (e.g. "🔴 99% used"). Renders the text unchanged when it holds no marker.
export function InlineSeverity({ text, size = 13 }: InlineSeverityProps): ReactNode {
  if (typeof text !== 'string' || !text) return (text as ReactNode) ?? null
  const out: ReactNode[] = []
  let last = 0
  let key = 0
  let m: RegExpExecArray | null
  const re = new RegExp(INLINE_SEV_SOURCE, 'gi')
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    const level = tok.slice(0, 4).toLowerCase() === 'sev:' ? tok.slice(4) : EMOJI_SEV[tok]
    out.push(<SeverityIcon key={key++} level={level} size={size} />)
    last = m.index + tok.length
  }
  if (!out.length) return text
  if (last < text.length) out.push(text.slice(last))
  return <>{out}</>
}
